import { useState } from 'react';
import { Bot, Wrench, Settings, MessageSquare, Info, FileText, Play, Brain, RotateCcw, CheckCircle, Search } from 'lucide-react';
import { AgentReply } from './MessageBlocks';
import PageAiGuide from './PageAiGuide';

const SA_HINTS = [
  { kind: 'explain', field: '推理模型', text: '须选「纯文本 + 支持 Function Calling」的模型；向量/重排模型不可用于决策。在【模型管理】启用后此处可选。' },
  { kind: 'recommend', field: '最大迭代轮数', text: 'ReAct 单次提问最多「思考→调用工具」的循环次数，过大易绕圈费 token、过小会半途而废。', value: '5' },
  { kind: 'explain', field: '降级策略', text: '工具/Agent 失败时的兜底：如实告知缺失（inform）/ 跳过继续 / 直接中止。建议 inform，避免臆造数据。' },
  { kind: 'explain', field: '路由候选 Agent', text: '所有已上架智能体自动纳入候选，无需勾选；启停在智能体管理处控制。路由准确度取决于各 Agent 的描述质量。' },
  { kind: 'explain', field: '决策提示词', text: '定义大脑如何「判断路由 vs 调工具、如何填参、如何交叉验证」；{{tools}} / {{agents}} 为运行时自动注入的占位符。' },
];

const AVAILABLE_TOOLS = [
  { id: 'alarm_query', name: '当前告警批量查询', category: '告警管理', bound: true },
  { id: 'alarm_detail', name: '告警详情查询', category: '告警管理', bound: true },
  { id: 'alarm_clear', name: '告警清除操作', category: '告警管理', bound: false },
  { id: 'alarm_stat', name: '告警统计报表', category: '告警管理', bound: false },
  { id: 'kpi_timeseries', name: 'KPI指标时序查询', category: 'KPI分析', bound: true },
  { id: 'kpi_baseline', name: 'KPI基线对比', category: 'KPI分析', bound: false },
  { id: 'kpi_topn', name: 'KPI TopN 排行', category: 'KPI分析', bound: false },
  { id: 'config_snapshot', name: '设备配置快照查询', category: '配置查询', bound: false },
  { id: 'config_diff', name: '配置变更比对', category: '配置查询', bound: false },
  { id: 'topo_path', name: '链路路径查询', category: '拓扑分析', bound: false },
  { id: 'topo_impact', name: '故障影响面分析', category: '拓扑分析', bound: false },
  { id: 'ticket_create', name: '工单创建', category: '工单管理', bound: false },
];

// 路由候选 = 所有已上架智能体（自动纳入，无需勾选）；启停在智能体管理处控制
const ONLINE_AGENTS = [
  { id: 'a1', name: '告警智能诊断', tags: '告警管理' },
  { id: 'a2', name: 'KPI异常分析', tags: 'KPI分析' },
  { id: 'a3', name: '工单处理助手', tags: '工单' },
  { id: 'a4', name: '网络拓扑查询', tags: '拓扑' },
  { id: 'a5', name: '配置合规检查', tags: '配置查询' },
  { id: 'a6', name: '容量评估助手', tags: 'KPI分析' },
  { id: 'a7', name: '光路故障诊断', tags: '告警管理' },
  { id: 'a8', name: '割接方案生成', tags: '变更管理' },
  { id: 'a9', name: '巡检报告助手', tags: '运维' },
];

const DEFAULT_PROMPT = `你是网管平台的超级智能体（路由决策大脑）。你的职责：
1. 分析用户问题意图，判断应「路由到下游 Agent」还是「调用注册工具」自行处理。
2. 调用工具时，严格按工具的 Function Calling Schema 填参；优先用问题中已给出的实体（设备ID/告警ID等）。
3. 多个工具结果需交叉验证后再下结论，禁止臆造数据。
4. 工具失败时按降级策略处理，并在最终回答中如实说明缺失部分。

可用工具：{{tools}}
可路由 Agent：{{agents}}（所有已上架智能体）`;

// 调试回复：复用会话界面同一套内容块结构
function buildDebugReply() {
  return [
    { type: 'step', index: 1, action: '查询当前告警', status: 'done', latency: '0.8s', children: [
      { type: 'thinking', duration: 2, status: 'done', content: '意图=告警根因分析；问题含设备 NE-HB-Core-01，可直接用工具，无需路由下游 Agent。' },
      { type: 'tool_call', tool: '当前告警批量查询', status: 'done', args: '{ "ne_id": "NE-HB-Core-01", "level": "critical" }' },
      { type: 'tool_result', resultType: 'json', content: '{\n  "count": 1,\n  "alarms": [{ "code": "LINK_DOWN", "time": "09:14:22" }]\n}' },
    ] },
    { type: 'step', index: 2, action: '查询端口流量 KPI', status: 'done', latency: '1.2s', children: [
      { type: 'thinking', duration: 3, status: 'done', content: '得到 LINK_DOWN，需 KPI 佐证 → 调用「KPI指标时序查询」。' },
      { type: 'tool_call', tool: 'KPI指标时序查询', status: 'done', args: '{ "ne_id": "NE-HB-Core-01", "metrics": ["port_traffic"] }' },
      { type: 'tool_result', resultType: 'log', content: '[09:14:22] port_traffic=0Gbps  ALARM\n[09:14:30] link_state=DOWN    ERROR' },
    ] },
    { type: 'answer', content: '该告警为物理链路中断（LINK_DOWN）。端口流量同步归零，排除配置类问题，初判物理层故障。\n\n（本次决策：调用工具×2，未路由下游 Agent，未触发降级）' },
  ];
}

function ConfigField({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function SuperAgentConsole() {
  const [tab, setTab] = useState('config');
  const [model, setModel] = useState('Qwen2.5-72B-Instruct');
  const [maxIter, setMaxIter] = useState(5);
  const [fallback, setFallback] = useState('inform');
  const [tools, setTools] = useState(AVAILABLE_TOOLS);
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [debugInput, setDebugInput] = useState('分析 NE-HB-Core-01 的 critical 告警根因');
  const [debugReply, setDebugReply] = useState(null);
  const [running, setRunning] = useState(false);

  const [toolQuery, setToolQuery] = useState('');
  const [toolCat, setToolCat] = useState('全部');
  const [agentQuery, setAgentQuery] = useState('');
  const toggleTool = (id) => setTools(prev => prev.map(t => t.id === id ? { ...t, bound: !t.bound } : t));
  const boundTools = tools.filter(t => t.bound).length;
  const toolCats = ['全部', ...Array.from(new Set(tools.map(t => t.category)))];
  const shownTools = tools.filter(t => (toolCat === '全部' || t.category === toolCat) && (!toolQuery || t.name.includes(toolQuery)));
  const shownAgents = ONLINE_AGENTS.filter(a => !agentQuery || a.name.includes(agentQuery) || a.tags.includes(agentQuery));

  const runDebug = () => {
    setRunning(true); setDebugReply(null);
    setTimeout(() => { setDebugReply(buildDebugReply()); setRunning(false); }, 900);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">超级智能体 · 工具调用引擎</h2>
          <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">三期 P0 核心</span>
        </div>
        <p className="text-sm text-gray-500">平台第一方智能体大脑。接收提问（可含上下文感知注入），自主决策路由或调用工具并合成答案。</p>
      </div>

      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-5">
        <button onClick={() => setTab('config')} className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'config' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          <Settings className="w-4 h-4" /> 引擎配置
        </button>
        <button onClick={() => setTab('prompt')} className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'prompt' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          <FileText className="w-4 h-4" /> 决策提示词 + 调试
        </button>
      </div>

      {tab === 'config' ? (
        <>
          <div className="flex items-center gap-2 p-3 mb-5 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-700">
            <MessageSquare className="w-4 h-4 flex-shrink-0" />
            <span>本页用于<strong>配置</strong>引擎。智能体实际执行过程（思考/工具调用/结果/最终回答）以「内容块」呈现在<strong>「会话界面」</strong>，并作为决策溯源与看板工具调用量的来源。</span>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4"><Settings className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-semibold text-gray-800">引擎配置</h3></div>
              <ConfigField label="驱动模型" hint="仅可选【模型管理】中标记支持 Function Calling 的纯文本模型">
                <select value={model} onChange={e => setModel(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option>Qwen2.5-72B-Instruct</option><option>GPT-4o</option><option>DeepSeek-V3</option>
                </select>
              </ConfigField>
              <ConfigField label={`最大工具调用轮次：${maxIter}`} hint="防止无限 ReAct 循环；超出后强制收敛并答复">
                <input type="range" min={1} max={10} value={maxIter} onChange={e => setMaxIter(+e.target.value)} className="w-full accent-indigo-600" />
              </ConfigField>
              <ConfigField label="工具调用失败降级" hint="对应会话界面内容块的「局部错误 / 整体失败」展示。原子能力唯一注册、无等价备选工具，故不提供「换工具」，只重试同一工具">
                <div className="space-y-1.5">
                  {[{ v: 'retry', label: '重试同一工具（指数退避，最多 2 次）' }, { v: 'skip', label: '跳过该工具，基于已有信息作答' }, { v: 'inform', label: '如实告知用户工具不可用' }].map(o => (
                    <label key={o.v} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"><input type="radio" name="fb" checked={fallback === o.v} onChange={() => setFallback(o.v)} className="accent-indigo-600" />{o.label}</label>
                  ))}
                </div>
              </ConfigField>
            </div>
            <div className="space-y-4">
              {/* 绑定工具：需显式 opt-in —— 搜索 + 分类筛选 + 滚动列表（应对数量多） */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><Wrench className="w-4 h-4 text-blue-500" /><h3 className="text-sm font-semibold text-gray-800">绑定工具（来自能力注册中心）</h3></div>
                  <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">已绑 {boundTools}/{tools.length}</span>
                </div>
                <p className="text-xs text-gray-400 mb-2">工具为原子能力，需显式勾选授予超级智能体调用权限</p>
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input value={toolQuery} onChange={e => setToolQuery(e.target.value)} placeholder="搜索工具名" className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                  </div>
                  <select value={toolCat} onChange={e => setToolCat(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 max-w-[120px]">
                    {toolCats.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto pr-1 border border-gray-100 rounded-lg p-1">
                  {shownTools.length === 0 ? <div className="text-center py-6 text-xs text-gray-300">无匹配工具</div> : shownTools.map(t => (
                    <label key={t.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <div className="flex items-center gap-2 min-w-0"><input type="checkbox" checked={t.bound} onChange={() => toggleTool(t.id)} className="accent-blue-600 flex-shrink-0" /><span className="text-sm text-gray-700 truncate">{t.name}</span></div>
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded flex-shrink-0 ml-2">{t.category}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Info className="w-3 h-3 flex-shrink-0" /> 仅显示已提供 FC Schema 的能力；列表可搜索/按分类筛选/滚动，工具增多也不撑爆页面</p>
              </div>

              {/* 路由候选 Agent：所有已上架自动纳入，只读 —— 搜索 + 滚动列表 */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><Bot className="w-4 h-4 text-indigo-500" /><h3 className="text-sm font-semibold text-gray-800">路由候选 Agent</h3></div>
                  <span className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">已上架 {ONLINE_AGENTS.length}</span>
                </div>
                <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                  <Info className="w-3 h-3 flex-shrink-0" /> 全部已上架智能体自动纳入，无需勾选；上下架在【智能体管理】控制
                </p>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input value={agentQuery} onChange={e => setAgentQuery(e.target.value)} placeholder="搜索智能体名 / 分类" className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div className="max-h-44 overflow-y-auto pr-1 border border-gray-100 rounded-lg divide-y divide-gray-50">
                  {shownAgents.length === 0 ? <div className="text-center py-6 text-xs text-gray-300">无匹配智能体</div> : shownAgents.map(a => (
                    <div key={a.id} className="flex items-center justify-between px-2.5 py-2 hover:bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0"><span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" /><span className="text-sm text-gray-700 truncate">{a.name}</span></div>
                      <span className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-500 rounded flex-shrink-0 ml-2">{a.tags}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* 左：提示词编辑 */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-semibold text-gray-800">决策提示词</h3></div>
              <button onClick={() => setPrompt(DEFAULT_PROMPT)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"><RotateCcw className="w-3 h-3" /> 恢复预设</button>
            </div>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={16}
              className="w-full px-3 py-2 text-xs font-mono leading-relaxed border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <p className="text-xs text-gray-400 mt-2">支持占位符 {'{{tools}}'} / {'{{agents}}'}，运行时注入已绑定工具与全部已上架 Agent 清单。建议由 AI 团队预置高质量模板。</p>
          </div>

          {/* 右：调试预览 —— 复用会话界面对话气泡 + 内容块样式 */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col">
            <div className="flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-indigo-500" /><h3 className="text-sm font-semibold text-gray-800">调试预览（配置态）</h3></div>

            {/* 对话气泡区：与会话界面同一套渲染（输入框在下方，体验一致） */}
            <div className="flex-1 bg-gradient-to-br from-blue-50/40 to-indigo-50/40 rounded-xl p-4 space-y-3 overflow-y-auto" style={{ minHeight: 320 }}>
              {!debugReply && !running ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm text-center">
                  <Brain className="w-7 h-7 mb-2 opacity-40" />
                  在下方输入模拟问题并运行，以与会话界面一致的对话样式预览决策过程
                  <span className="text-xs mt-1">（用于调提示词，效果与终端用户所见一致）</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-end">
                    <div className="max-w-xs px-4 py-2.5 rounded-2xl text-sm bg-blue-600 text-white">{debugInput}</div>
                  </div>
                  {running ? (
                    <div className="flex items-center gap-2 text-xs text-gray-400"><Brain className="w-4 h-4 animate-pulse" /> 决策中…</div>
                  ) : (
                    <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-indigo-500 mb-2 font-medium"><Bot className="w-3.5 h-3.5" /> 超级智能体</div>
                      <AgentReply blocks={debugReply} />
                      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle className="w-3 h-3" /> 调试完成，可调整提示词后重跑对比决策差异
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 输入框：固定在下方，与正常会话界面一致 */}
            <div className="flex gap-2 mt-3">
              <input value={debugInput} onChange={e => setDebugInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !running && runDebug()} placeholder="输入模拟问题…"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <button onClick={runDebug} disabled={running} className={`px-4 py-2 text-xs font-medium rounded-lg whitespace-nowrap ${running ? 'bg-gray-100 text-gray-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                {running ? '调试中…' : '运行调试'}
              </button>
            </div>
          </div>
        </div>
      )}
      <PageAiGuide pageName="超级智能体" hints={SA_HINTS} />
    </div>
  );
}
